"use client"

import { useEffect, useRef, useState } from "react"
import { AlertTriangle, Check } from "lucide-react"
import { ToolPageShell, ToolProductCarousel, ToolShareBar } from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// Sources:
//   • NSF/ANSI 12 — Automatic ice-making equipment sanitation standard.
//   • Manufacturer service manuals — Hoshizaki KM-series, Manitowoc IceSeries,
//     Scotsman Prodigy: cleaning intervals, descaling, water-filter cycles.
//   • Whirlpool / GE / LG residential refrigerator use-and-care guides:
//     6-month water-filter replacement, periodic ice-bin sanitation, throw
//     out first batch after extended outage.
//   • FDA Food Code (2022) §4-602.11: ice-contact surfaces require regular
//     cleaning; food-grade ice equipment surfaces sanitized at intervals
//     specified by manufacturer.

const SECTIONS = [
  { id: "why", t: "Why it matters" },
  { id: "schedule", t: "The schedule" },
  { id: "deep", t: "Deep-clean steps" },
  { id: "filter", t: "Water filter" },
  { id: "signs", t: "Warning signs" },
] as const

export function IceMakerMaintenanceSchedulerPageClient() {
  const [active, setActive] = useState<string>(SECTIONS[0].id)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          const topmost = visible.reduce((prev, curr) =>
            prev.boundingClientRect.top < curr.boundingClientRect.top ? prev : curr,
          )
          setActive(topmost.target.id)
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    )
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observerRef.current?.observe(el)
    })
    return () => observerRef.current?.disconnect()
  }, [])

  const jumpTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 96
    window.scrollTo({ top: y, behavior: "smooth" })
    setActive(id)
    // Keep the URL hash in sync so the entry is a real, shareable anchor
    // (plan §0 item 179a). replaceState avoids the native hash-jump.
    history.replaceState(null, "", `#${id}`)
  }

  return (
    <ToolPageShell
      toolName="Ice Maker Maintenance Schedule"
      heroEyebrow="Maintenance"
      heroTitlePre="Ice that doesn't"
      heroTitleItalic="taste"
      heroTitlePost="like the freezer."
      heroSubtitle="Ice makers concentrate whatever's in your water — and grow biofilm in the parts you can't see. Here's the manufacturer + NSF maintenance schedule that keeps cubes clean, cold, and on-spec."
      heroMeta={["⏱ ~6 min read", "NSF + manufacturer service guides"]}
      heroShare={<ToolShareBar title="Ice Maker Maintenance Schedule" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-900 text-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-[#ff9951]">
            The two anchor intervals
          </div>
          <p
            className="text-[17px] font-medium leading-[1.45] mb-5"
            style={{ textWrap: "balance" as never }}
          >
            Replace the water filter every{" "}
            <em className="italic text-[#ff9951]">6 months</em>. Run a full descale + sanitize{" "}
            <em className="italic text-[#ff9951]">every 6 months</em> — quarterly on hard water.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Filter
              </div>
              <div className="text-[22px] font-black tracking-tight">6 mo</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Deep clean
              </div>
              <div className="text-[22px] font-black tracking-tight">6 mo</div>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "Can I just use vinegar instead of an ice machine cleaner?",
          answer:
            "Vinegar will descale mineral buildup, but it does not sanitize. Manufacturer service manuals (Hoshizaki, Manitowoc, Scotsman) and residential brands like Whirlpool and GE call for a nickel-safe descaler followed by a sanitizer rated for food-contact surfaces. Many residential ice machines void the warranty if anything other than the specified cleaner is used.",
        },
        {
          question: "How often do I really need to replace the water filter?",
          answer:
            "Whirlpool, GE, LG, Samsung, and Frigidaire all specify 6 months or 200–300 gallons, whichever comes first. The flow restriction warning light isn't a freshness gauge — it's measuring pressure drop. Replace on the calendar.",
        },
        {
          question: "Should I throw out ice after a power outage?",
          answer:
            "Yes — discard the existing batch and the next batch the unit produces. USDA and manufacturer guidance treat ice as you'd treat any food held above 40°F: if you can't confirm the temperature held, throw it out.",
        },
        {
          question: "My ice tastes funny but the filter is new — what's wrong?",
          answer:
            "Almost always biofilm in the ice mold or bin. Sanitize per the deep-clean steps below. If the bin door doesn't seal, ambient air carries freezer odors into the ice; check the gasket.",
        },
        {
          question: "Why is my production rate dropping?",
          answer:
            "Three primary causes per service-manual diagnostics: scaled water valve or fill tube (descale); clogged condenser coil (vacuum the coil); or a marginal water-supply pressure (manufacturers typically require 20–120 psi at the inlet).",
        },
      ]}
      relatedTools={[
        { title: "Refrigerator Maintenance Schedule", category: "Maintenance", description: "DOE-aligned full-fridge schedule.", href: "/tools/refrigerator-maintenance-schedule", time: "~5 min" },
        { title: "Door Seal Test", category: "Maintenance", description: "Check the gasket protecting your ice.", href: "/tools/refrigerator-door-seal-test", time: "~3 min" },
        { title: "Energy Cost Calculator", category: "Energy & Cost", description: "What ice production adds to your bill.", href: "/tools/energy-cost-calculator", time: "~30 sec" },
        { title: "Refrigerator Lifespan", category: "Specialty", description: "Years left on the unit.", href: "/tools/refrigerator-lifespan-calculator", time: "~45 sec" },
      ]}
      cta={{
        title: "Old ice maker giving up?",
        body: "If production is slow even after a full descale and the unit is past 10 years, replacement is usually cheaper than repair. Browse refrigerators with built-in ice makers.",
        buttonLabel: "Browse refrigerators with ice makers",
        buttonHref: "/collections/french-door-refrigerators",
      }}
    >
      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid md:grid-cols-[170px_1fr] gap-8 md:gap-10">
          <aside className="hidden md:block">
            <div className="sticky top-[96px]">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                On this page
              </div>
              <nav className="border-l border-gray-200" aria-label="Section navigation">
                {SECTIONS.map((s) => {
                  const isActive = active === s.id
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        jumpTo(s.id)
                      }}
                      aria-current={isActive ? "location" : undefined}
                      className={[
                        "block cursor-pointer py-2 pl-3 -ml-px transition-all text-left w-full",
                        isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-700",
                      ].join(" ")}
                      style={{
                        borderLeft: isActive ? "2px solid #ff9951" : "2px solid transparent",
                      }}
                    >
                      <div className="text-[13px] font-bold leading-tight">{s.t}</div>
                    </a>
                  )
                })}
              </nav>
            </div>
          </aside>

          <article>
            <GuideTool
              eyebrow="Interactive schedule builder"
              title="Build your cleaning schedule"
              intro="Water hardness and workload set the interval. Answer three questions and get the schedule your machine actually needs."
              questions={[
                            {
                              id: "water",
                              label: "What water feeds it?",
                              options: [
                                { value: "hard", label: "Hard, unfiltered tap" },
                                { value: "soft", label: "Filtered or soft water" }
                              ],
                            },
                            {
                              id: "use",
                              label: "How hard does it work?",
                              options: [
                                { value: "heavy", label: "Daily, family or entertaining" },
                                { value: "light", label: "Occasional" }
                              ],
                            },
                            {
                              id: "kind",
                              label: "What kind of machine?",
                              options: [
                                { value: "portable", label: "Portable countertop" },
                                { value: "built", label: "Built-in / under-counter" }
                              ],
                            }
              ]}
              compute={(a) => {
                            const descale = a.water === "hard" ? (a.use === "heavy" ? "monthly" : "every 2 months") : a.use === "heavy" ? "every 2\u20133 months" : "quarterly"
                            const deep = a.kind === "built" ? "Run the manufacturer clean cycle with nickel-safe cleaner at the same interval, and change the inline water filter every 6 months \u2014 scale you keep out beats scale you remove." : "Empty, rinse, and dry it between uses \u2014 standing water is where scale and slime start on portables \u2014 and run a vinegar or nickel-safe descale at the interval above."
                            const tone = a.water === "hard" && a.use === "heavy" ? "warn" : "good"
                            return { tone, verdict: "Descale " + descale, detail: deep + " Wipe the ice bin weekly with mild soap either way." }
                          }}
              ctaLabel="Build my schedule"
              footnote="Intervals follow the hardness/usage matrix in the guide below."
            />
            <section id="why" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Why ice makers need a real schedule
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                An ice maker is a low-temperature, low-light, high-moisture environment with a
                continuous flow of food-contact water. NSF/ANSI 12 — the sanitation standard that
                applies to commercial ice machines and informs residential service intervals —
                exists because two specific failure modes are nearly universal: mineral scale on
                the evaporator and biofilm in the bin and fill tube.
              </p>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Both develop quietly. By the time you can taste them, they&apos;re also slowing
                production and shortening the unit&apos;s service life.
              </p>

              <div
                className="rounded-xl p-5 md:p-6 my-8 flex gap-4 md:gap-5"
                style={{ background: "rgba(255,153,81,0.06)", border: "1px solid rgba(255,153,81,0.25)" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#ff9951] text-[var(--color-brand-action-text)] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-black text-[17px] mb-1 text-gray-900">
                    Power-outage rule
                  </div>
                  <p className="text-[14px] text-gray-700 leading-[1.6]">
                    USDA: discard ice that may have warmed above 40°F. After any extended outage
                    (or if the freezer cycled warm), dump the bin <strong>and</strong> the first
                    batch the unit makes after recovery.
                  </p>
                </div>
              </div>
            </section>

            <section id="schedule" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                The maintenance schedule
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Intervals consolidated from Whirlpool, GE, LG, and Samsung use-and-care guides
                plus Hoshizaki / Manitowoc service intervals. Halve the descale and sanitize
                intervals if you&apos;re on hard water (above ~7 gpg / 120 ppm).
              </p>
              <div className="overflow-x-auto rounded-xl border border-gray-900">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Interval
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Task
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Monthly", "Empty bin, wash with warm soapy water, dry, reinstall", "~10 min"],
                      ["Monthly", "Inspect water-supply line for kinks, drips, frost", "~5 min"],
                      ["Quarterly", "Vacuum condenser coil (if accessible)", "~10 min"],
                      ["Every 6 months", "Replace water filter (or 200–300 gal, whichever first)", "~10 min"],
                      ["Every 6 months", "Descale + sanitize per manufacturer cleaner", "~45 min"],
                      ["Every 3 months (hard water)", "Descale + sanitize", "~45 min"],
                      ["Annually", "Inspect water inlet valve / clean inlet screen", "~20 min"],
                      ["Annually", "Test 24-hour ice production vs nameplate spec", "24 hr passive"],
                    ].map((r, i, arr) => (
                      <tr
                        key={`${r[0]}-${r[1]}`}
                        className={i % 2 ? "bg-gray-50" : "bg-white"}
                        style={{ borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none" }}
                      >
                        <td className="px-5 py-4 font-extrabold tracking-tight">{r[0]}</td>
                        <td className="px-5 py-4 text-gray-700">{r[1]}</td>
                        <td className="px-5 py-4">
                          <span className="font-mono font-bold text-[#ff9951]">{r[2]}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="deep" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                The 6-month deep clean
              </h2>
              <ol className="list-none p-0 m-0 border-t border-gray-900">
                {[
                  { n: "01", t: "Power off and shut the water supply", d: "Switch the unit off at the panel and close the water shutoff valve. Wait for the current cycle to finish if the harvest is mid-drop." },
                  { n: "02", t: "Empty and dispose of the bin", d: "Throw out all existing ice — you'll be running cleaner through the system. Wash the bin with warm soapy water, rinse thoroughly, dry." },
                  { n: "03", t: "Run the manufacturer's descaler", d: "Use the cleaner specified in your model's manual (nickel-safe descaler for residential evaporators). Follow dose and dwell time exactly — too short leaves scale, too long can pit the evaporator." },
                  { n: "04", t: "Sanitize", d: "Follow the descaler with the matched sanitizer. NSF-listed residential sanitizers are formulated for food-contact ice surfaces. Run the rinse cycle until the bin is empty of any chemical residue." },
                  { n: "05", t: "Replace the filter, then dump first batches", d: "Install a fresh water filter. Restart the unit. Discard the first 2–3 production batches — they'll carry residual cleaner and air-trapped fines." },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start py-5 sm:py-6 border-b border-gray-900"
                  >
                    <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] pt-1 text-[#ff9951]">
                      {s.n}
                    </div>
                    <div>
                      <h3
                        className="text-gray-900"
                        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                      >
                        {s.t}
                      </h3>
                      <p className="mt-1.5 text-[15px] text-gray-600 leading-[1.55]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section id="filter" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Water filter — the one task to never skip
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                The water filter does two things at once: it removes chlorine and sediment so the
                ice tastes neutral, and it traps the particulates that would otherwise scale your
                evaporator. Both functions are time-limited. After the rated capacity, the
                activated carbon is exhausted — water still flows, but unfiltered.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Replace every 6 months — calendar reminder, not the panel light",
                  "Or every 200–300 gallons, whichever comes first",
                  "Use the manufacturer's part number (or NSF/ANSI 42-certified equivalent)",
                  "Flush 2–3 gallons through after install before drinking ice water",
                  "Cut frequency in half on well water or a known-poor municipal source",
                  "Don't store new filters above 100°F — degrades the carbon",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50">
                    <div className="w-6 h-6 rounded-full bg-[#0469f0] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] text-gray-700 leading-[1.5]">{t}</span>
                  </div>
                ))}
              </div>
            </section>

            <section id="signs" className="scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Warning signs — and what they usually mean
              </h2>
              <div className="overflow-x-auto rounded-xl border border-gray-900">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Symptom
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Most likely cause
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Fix
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Cloudy or hollow cubes", "Mineral scale on evaporator", "Descale per manual"],
                      ["Off taste / smell", "Biofilm in bin or fill tube", "Sanitize cycle"],
                      ["Slow production", "Scaled water valve or dirty condenser", "Descale + vacuum coil"],
                      ["Small cubes", "Low water pressure or partial valve clog", "Check inlet pressure / clean screen"],
                      ["No ice at all", "Water shutoff closed, filter exhausted, or harvest sensor", "Verify supply, change filter, then service call"],
                      ["Leaking water", "Cracked fill tube or loose supply fitting", "Inspect line and reseat fittings"],
                    ].map((r, i, arr) => (
                      <tr
                        key={r[0]}
                        className={i % 2 ? "bg-gray-50" : "bg-white"}
                        style={{ borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none" }}
                      >
                        <td className="px-5 py-4 font-extrabold tracking-tight">{r[0]}</td>
                        <td className="px-5 py-4 text-gray-700">{r[1]}</td>
                        <td className="px-5 py-4 text-gray-600">{r[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </article>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Built-in ice makers"
          title="French-door refrigerators to compare"
          description="Through-door dispensers and in-freezer ice makers need the most upkeep. Check each model's features on its page."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
